/**
 * Roadmap / Learn track subscription rules.
 * Programming Fundamentals stays available without a subscription; other published tracks require premium.
 *
 * Free access matches any slug ending with `*-programming-fundamentals` (e.g. `python-programming-fundamentals`,
 * `javascript-programming-fundamentals`), the canonical free Python PF id below, or catalog titles that indicate
 * "Programming Fundamentals".
 */

/** Canon free PF slug for UX fallbacks where a single demo id helps (Firestore uses per-language PF tracks). */
export const ROADMAP_FREE_PUBLIC_TRACK_ID = 'python-programming-fundamentals';

function normalizeSlugForFreeTrackMatch(trackId) {
  return String(trackId ?? '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-');
}

function normalizeFreeTierTrackTitle(title) {
  return String(title ?? '')
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * Title indicates the shared free curriculum (covers localized/per-language display titles).
 * @param {string|null|undefined} title
 */
function titleIndicatesFreeProgrammingFundamentals(title) {
  const s = normalizeFreeTierTrackTitle(title);
  if (!s) return false;
  return /\bprogramming\s+fundamentals?\b/.test(s);
}

/**
 * Whether a Firestore track id denotes the free Programming Fundamentals curriculum for any language.
 * @param {string|null|undefined} trackId
 * @returns {boolean}
 */
export function isFreePublicRoadmapTrackId(trackId) {
  if (trackId == null || String(trackId).trim() === '') return false;
  const s = normalizeSlugForFreeTrackMatch(trackId);
  if (s === normalizeSlugForFreeTrackMatch(ROADMAP_FREE_PUBLIC_TRACK_ID)) return true;
  if (s.endsWith('-programming-fundamentals')) return true;
  return false;
}

/**
 * Use full catalog row when available so title-only matches work alongside slug rules.
 * @param {Record<string, unknown>|null|undefined} track
 * @returns {boolean}
 */
export function isRoadmapFreePublicTrack(track) {
  if (!track || typeof track !== 'object') return false;
  const tid = track.trackId != null ? String(track.trackId).trim() : '';
  if (tid && isFreePublicRoadmapTrackId(tid)) return true;
  const title = track.title ?? track.name;
  return titleIndicatesFreeProgrammingFundamentals(title);
}

/**
 * @param {boolean} isPremiumUser
 * @param {string|null|undefined} trackId - Firestore track slug (e.g. content catalog trackId)
 * @param {Record<string, unknown>|null|undefined} [track] - optional catalog row for title-based match
 * @returns {boolean}
 */
export function userCanAccessRoadmapTrack(isPremiumUser, trackId, track = null) {
  if (isPremiumUser) return true;
  if (track && isRoadmapFreePublicTrack(track)) return true;
  if (trackId == null || String(trackId).trim() === '') return false;
  return isFreePublicRoadmapTrackId(trackId);
}

/**
 * Track slug from a user cookbook doc (roadmap decks). Falls back to tag shape from createCookbookFromNode.
 * @param {Record<string, unknown>|null|undefined} data
 * @returns {string|null}
 */
export function getRoadmapTrackIdFromUserDeckData(data) {
  if (!data || typeof data !== 'object') return null;
  const direct = data.roadmapTrackId;
  if (direct != null && String(direct).trim()) return String(direct).trim();
  const tags = data.tags;
  if (Array.isArray(tags) && tags.length >= 3 && tags[0] === 'roadmap' && tags[1] === 'cookbook') {
    const t = tags[2];
    if (t != null && String(t).trim()) return String(t).trim();
  }
  return null;
}
